/**
 * Cert-material upload for an Peer Interface (ADR-036 OQ-2, resolved
 * 2026-07-24; ADR-015 key custody).
 *
 * **Public material only.** The control accepts a Consumer CA bundle or the
 * Meridian leaf certificate. It does not — and must not — accept a private key:
 * Meridian's keypair stays minted at install by ``tools/meridian_certgen.py``
 * and is referenced by path, because a key routed through nginx, the BFF and
 * application memory cannot afterwards be *proven* uncopied, and key custody is
 * a documented control in ``security.md`` whose change would trigger
 * re-accreditation. Where a Consumer's own PKI must issue Meridian's egress
 * identity, the path is CSR generation, not a cert+key upload.
 *
 * A CA bundle decides *who Meridian is willing to send mission data to*, so the
 * BFF validates the X.509, bounds the size, writes only a server-generated
 * filename, audits the upload, and echoes subject / issuer / validity /
 * SHA-256 fingerprint. This component surfaces that echo prominently: the point
 * is that the operator verifies it **out of band** against what the Consumer's
 * operator told them, rather than trusting the transfer.
 *
 * Cross-references:
 *   - ADR-036 OQ-2; ADR-015 (cert material referenced by path, never inlined).
 *   - REQ-PEER-043 (privileged), -044 (audited ConfigurationChangeEvent).
 *   - Flow W §6 (install); ``security.md`` "Direct peer-manager (mtls-tcp)".
 */
import { useRef, useState } from "react";
import {
  useUploadCertMaterial,
  type CertMaterialKind,
  type CertMaterialUploadResult,
} from "@/peers/use-peer-interfaces";

/** Props for {@link PeerCertUpload}. */
export interface PeerCertUploadProps {
  kind: CertMaterialKind;
  /** Called with the server-generated path so the caller can set the reference field. */
  onStored: (storedPath: string) => void;
}

/** Render the upload button + the parsed-certificate echo. */
export function PeerCertUpload({ kind, onStored }: PeerCertUploadProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<CertMaterialUploadResult | null>(null);
  const mutation = useUploadCertMaterial();

  const handleFile = (file: File | undefined): void => {
    if (file === undefined) return;
    mutation.mutate(
      { kind, file },
      {
        onSuccess: (uploaded) => {
          setResult(uploaded);
          onStored(uploaded.stored_path);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-1" data-testid={`peer-cert-upload-${kind}`}>
      <input
        ref={inputRef}
        type="file"
        accept=".pem,.crt,.cer,application/x-pem-file"
        className="hidden"
        data-testid={`peer-cert-file-${kind}`}
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={mutation.isPending}
          data-testid={`peer-cert-upload-button-${kind}`}
          className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated disabled:opacity-50"
        >
          {mutation.isPending ? "Uploading…" : "Upload PEM…"}
        </button>
        <span className="text-meta text-text-muted">Public certificate material only.</span>
      </div>

      {mutation.isError && (
        <p
          role="alert"
          data-testid={`peer-cert-error-${kind}`}
          className="text-meta text-destructive"
        >
          {mutation.error.status === 503
            ? "This deployment has no writable cert volume — pre-stage the file and reference it by path instead."
            : `Rejected: ${describeRejection(mutation.error.body)}`}
        </p>
      )}

      {result !== null && (
        <div
          data-testid={`peer-cert-result-${kind}`}
          className="rounded border border-border bg-bg-base p-2 text-meta"
        >
          <p className="pb-1 text-text-secondary">
            Stored as <span className="font-mono text-text-primary">{result.stored_path}</span>.
            Verify the fingerprint out of band before starting the interface.
          </p>
          <ul className="flex flex-col gap-1">
            {result.certificates.map((certificate) => (
              <li key={certificate.fingerprint_sha256} className="font-mono text-text-primary">
                <div>{certificate.subject}</div>
                <div className="text-text-secondary">issued by {certificate.issuer}</div>
                <div className="text-text-secondary">
                  {certificate.not_before} → {certificate.not_after}
                  {certificate.is_ca ? " · CA" : ""}
                </div>
                <div className="break-all text-text-secondary">
                  SHA-256 {certificate.fingerprint_sha256}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Pull the BFF's typed rejection reason out of an error body, without leaking internals. */
function describeRejection(body: unknown): string {
  if (typeof body === "object" && body !== null && "message" in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "the file is not valid certificate material.";
}
